import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, ArrowLeft, Ban, CheckCircle, AlertTriangle, Shield, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [offensiveWords, setOffensiveWords] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  useEffect(() => {
    if (user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bizRes, convRes, wordsRes] = await Promise.all([
        axios.get(`${API}/admin/businesses`, { withCredentials: true }),
        axios.get(`${API}/admin/conversations`, { withCredentials: true }),
        axios.get(`${API}/admin/offensive-words`, { withCredentials: true }),
      ]);
      setBusinesses(bizRes.data);
      setConversations(convRes.data);
      setOffensiveWords(wordsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const handleSuspendBusiness = async (businessId) => {
    try {
      await axios.post(`${API}/admin/businesses/${businessId}/suspend`, {}, { withCredentials: true });
      toast.success('Business status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const handleVerifyBusiness = async (businessId) => {
    try {
      await axios.post(`${API}/admin/businesses/${businessId}/verify`, {}, { withCredentials: true });
      toast.success('Business verification status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify business');
    }
  };

  const handleViewMessages = async (conversation) => {
    try {
      const response = await axios.get(
        `${API}/admin/conversations/${conversation.conversation_id}/messages`,
        { withCredentials: true }
      );
      setConversationMessages(response.data);
      setSelectedConversation(conversation);
      setShowMessagesModal(true);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleBanUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, {}, { withCredentials: true });
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleAddWord = async (e) => {
    e.preventDefault();
    if (!newWord.trim()) return;

    try {
      await axios.post(`${API}/admin/offensive-words`, { word: newWord }, { withCredentials: true });
      toast.success('Word added to filter');
      setNewWord('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add word');
    }
  };

  const handleDeleteWord = async (wordId) => {
    try {
      await axios.delete(`${API}/admin/offensive-words/${wordId}`, { withCredentials: true });
      toast.success('Word removed from filter');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove word');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <div className="container mx-auto px-6 md:px-12 py-12">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Dashboard
        </Button>

        <h1 className="text-4xl font-outfit font-bold text-primary mb-8">Admin Dashboard</h1>

        <Tabs defaultValue="businesses" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="businesses" data-testid="businesses-tab">Businesses</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="conversations-tab">Conversations</TabsTrigger>
            <TabsTrigger value="moderation" data-testid="moderation-tab">Moderation</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses" data-testid="businesses-content">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businesses.map((biz) => (
                    <TableRow key={biz.business_id}>
                      <TableCell className="font-medium">{biz.name}</TableCell>
                      <TableCell>{biz.owner?.name}</TableCell>
                      <TableCell>{biz.industry}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            biz.is_suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {biz.is_suspended ? 'Suspended' : 'Active'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            biz.is_verified ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {biz.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyBusiness(biz.business_id)}
                            className="mr-2"
                            data-testid={`verify-business-${biz.business_id}`}
                          >
                            {biz.is_verified ? <Ban className="h-4 w-4" /> : <Shield className="h-4 w-4 text-cyan-600" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSuspendBusiness(biz.business_id)}
                            className="mr-2"
                            data-testid={`suspend-business-${biz.business_id}`}
                          >
                            {biz.is_suspended ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBanUser(biz.user_id)}
                            data-testid={`ban-user-${biz.user_id}`}
                          >
                            {biz.owner?.is_banned ? 'Unban' : 'Ban'} User
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="conversations" data-testid="conversations-content">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
              {conversations.map((conv) => (
                <div key={conv.conversation_id} className="border-b border-slate-200 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-outfit font-semibold text-primary">
                      {conv.participants_info.map((p) => p.name).join(' & ')}
                    </h3>
                    <div className="flex items-center gap-2">
                      {conv.flagged_count > 0 && (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {conv.flagged_count} flagged
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewMessages(conv)}
                        data-testid={`view-messages-${conv.conversation_id}`}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Messages
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {conv.recent_messages?.slice(0, 3).map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`text-sm p-2 rounded ${msg.is_flagged ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}
                      >
                        <span className="font-medium">{msg.sender_name}:</span> {msg.content}
                        {msg.is_flagged && <span className="text-red-600 ml-2">⚠️</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Messages Modal */}
            <Dialog open={showMessagesModal} onOpenChange={setShowMessagesModal}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-outfit font-bold text-primary">
                    Conversation Messages
                    {selectedConversation && (
                      <span className="text-sm font-normal text-slate-600 ml-2">
                        ({selectedConversation.participants_info.map(p => p.name).join(' & ')})
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-4">
                  {conversationMessages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`p-4 rounded-lg ${
                        msg.is_flagged 
                          ? 'bg-red-50 border-2 border-red-300' 
                          : 'bg-slate-50 border border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-semibold text-primary">{msg.sender_name}</span>
                          <span className="text-xs text-slate-500 ml-2">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        {msg.is_flagged && (
                          <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            FLAGGED
                          </span>
                        )}
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="moderation" data-testid="moderation-content">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-2xl font-outfit font-semibold text-primary mb-6">Offensive Words Filter</h2>
              <form onSubmit={handleAddWord} className="flex gap-2 mb-6" data-testid="add-word-form">
                <Input
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder="Add offensive word..."
                  className="flex-1"
                  data-testid="word-input"
                />
                <Button type="submit" className="bg-accent-orange" data-testid="add-word-button">
                  Add Word
                </Button>
              </form>
              <div className="flex flex-wrap gap-2">
                {offensiveWords.map((word) => (
                  <div
                    key={word.word_id}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm flex items-center gap-2"
                    data-testid={`word-badge-${word.word_id}`}
                  >
                    {word.word}
                    <button
                      onClick={() => handleDeleteWord(word.word_id)}
                      className="hover:text-red-900"
                      data-testid={`delete-word-${word.word_id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AdminDashboard;
